import type { ColDef } from "ag-grid-enterprise";
import { read, utils } from "xlsx";

// Multi-sheet representation for the viewer (ag-grid). One entry per worksheet.
export interface SheetData {
  name: string;
  rows: Record<string, unknown>[];
  columns: ColDef[];
}

// Compact single-sheet representation for thumbnails.
export interface SheetPreview {
  headers: string[];
  rows: string[][];
}

export interface SheetPreviewOptions {
  maxRows?: number;
  maxCols?: number;
}

const DEFAULT_MAX_ROWS = 5;
const DEFAULT_MAX_COLS = 6;

const isXlsx = (filename?: string) => /\.xlsx?$/i.test(filename ?? "");
const isTsv = (filename?: string) => /\.tsv$/i.test(filename ?? "");

// xlsx's delimiter auto-detection picks `,` for CSV reliably but doesn't
// always pick `\t` for TSV — fall back to the filename hint.
const getFieldSeparator = (filename?: string, delimiter?: string): string | undefined => {
  if (delimiter) return delimiter;
  if (isTsv(filename)) return "\t";
  return undefined;
};

const toCellString = (value: unknown): string => (value == null ? "" : String(value));

// xlsx returns Date instances for date-formatted cells when cellDates: true.
// Dates are non-serializable in Redux state (RTK Query stores parser output in
// the store), so flatten them to a locale string at parse time. Anything else
// passes through unchanged.
const serializeCell = (value: unknown): unknown =>
  value instanceof Date ? value.toLocaleDateString() : value;

// Shared workbook reader. `delimiter` overrides the filename-based detection
// (used by the preview parser when called with a raw text snippet).
const readWorkbook = (
  input: ArrayBuffer | string,
  filename: string | undefined,
  delimiter: string | undefined,
) => {
  const useExcelDates = isXlsx(filename);
  const FS = getFieldSeparator(filename, delimiter);
  return read(input, {
    type: typeof input === "string" ? "string" : "array",
    ...(useExcelDates ? { cellDates: true } : { raw: true }),
    ...(FS ? { FS } : {}),
  });
};

// Full multi-sheet parse — used by the FileViewerPanel's SpreadsheetViewer.
// Accepts xlsx binaries or csv/tsv text (via ArrayBuffer); the viewer fetches
// the file and passes the ArrayBuffer through unchanged.
export const parseSpreadsheet = (
  arrayBuffer: ArrayBuffer,
  filename?: string,
): SheetData[] => {
  const workbook = readWorkbook(arrayBuffer, filename, undefined);
  const useExcelDates = isXlsx(filename);

  return workbook.SheetNames.map((sheetName) => {
    const worksheet = workbook.Sheets[sheetName]!;
    const headers =
      utils.sheet_to_json<string[]>(worksheet, {
        header: 1,
      })[0] ?? [];

    const jsonData = utils.sheet_to_json<Record<string, unknown>>(worksheet, {
      defval: "",
      ...(useExcelDates ? { raw: true } : {}),
    });

    const rows = useExcelDates
      ? jsonData.map((row) => {
          const serialized: Record<string, unknown> = {};
          for (const key in row) {
            serialized[key] = serializeCell(row[key]);
          }
          return serialized;
        })
      : jsonData;

    const columns: ColDef[] = headers
      .filter((header) => header != null && header !== "")
      .map((header) => ({
        field: String(header),
        headerName: String(header),
        minWidth: 120,
        resizable: true,
        sortable: true,
        filter: true,
      }));

    return { name: sheetName, rows, columns };
  });
};

// Compact preview from a text snippet — used by thumbnails for CSV/TSV files.
// When `truncated` is true (Range-limited download cut mid-line) the trailing
// partial line is dropped; when false the snippet is the complete file body
// and every line is real data. Returns the first sheet sliced to maxRows × maxCols.
export const parseSheetPreview = (
  text: string,
  delimiter: string,
  truncated: boolean,
  { maxRows = DEFAULT_MAX_ROWS, maxCols = DEFAULT_MAX_COLS }: SheetPreviewOptions = {},
): SheetPreview => {
  let safeText = text;
  if (truncated) {
    const lastNewline = text.lastIndexOf("\n");
    // No `\n` at all means the whole buffer is a single partial line (e.g. a
    // very long header row, or legacy \r-only line endings cut mid-record).
    // We can't recover anything trustworthy from it.
    if (lastNewline < 0) return { headers: [], rows: [] };
    safeText = text.slice(0, lastNewline);
  }
  if (!safeText.trim()) return { headers: [], rows: [] };

  const workbook = readWorkbook(safeText, undefined, delimiter);
  const firstSheetName = workbook.SheetNames[0];
  const sheet = firstSheetName ? workbook.Sheets[firstSheetName] : undefined;
  if (!sheet) return { headers: [], rows: [] };

  const aoa = utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
    raw: true,
    defval: "",
  });
  if (aoa.length === 0) return { headers: [], rows: [] };

  const headers = (aoa[0] ?? []).slice(0, maxCols).map(toCellString);
  const rows = aoa
    .slice(1, 1 + maxRows)
    .map((row) => row.slice(0, maxCols).map(toCellString));
  return { headers, rows };
};
